import React, { useState, useCallback, useMemo } from "react";
import { useHistory, useRouteMatch } from "react-router";

import Tippy from "@tippy.js/react";
import clsx from "clsx";

import { MainMenu, Clickable } from "@plex/react-components";

import { IMessageId } from "infrastructure/localization";
import { IAuthorizationData } from "ui/app/user-provider";
import { useLocalizer, useUserContext, useAppContext } from "ui/hooks";

import styles from "./TopNavigationMenu.module.scss";

import { getMenuItems } from ".";
import "./TopNavigationMenu.scss";

export interface IMenuItems {
  groupedList: IMenuItem[];
  planeList: IMenuItem[];
}

export interface IMenuItem {
  id: string;
  icon?: React.ReactNode;
  title: IMessageId;
  children: IMenuItem[];
  path: string;
  requiredAuthorizationData?: IAuthorizationData[];
  isGroup?: boolean;
}

interface IMenuItemRouteData {
  id: string;
  path: string;
}

export const TopNavigationMenu: React.FC = () => {
  const localize = useLocalizer();
  const history = useHistory();
  const routeMatch = useRouteMatch();

  const appContext = useAppContext();
  const userContext = useUserContext();

  const [menuItems] = useState(() => getMenuItems(appContext.routes));
  const [isOpened, setIsOpened] = useState(false);
  const activeMenuItem = useMemo(() => menuItems.planeList.filter(e => routeMatch && e.path === routeMatch.path)[0], [menuItems.planeList, routeMatch]);

  const [activeMenuItemId, setActiveMenuItemId] = useState(activeMenuItem && activeMenuItem.id);

  const onMainMenuItemClick = useCallback(
    (e, data) => {
      e.preventDefault();
      const routeData = data as IMenuItemRouteData;

      if (routeData && routeData.path && routeData.id) {
        if (history.location.pathname !== routeData.path) {
          history.push(routeData.path);
          setActiveMenuItemId(routeData.id);
        }
      }
    },
    [history]
  );

  const onHide = useCallback(() => {
    setIsOpened(false);
  }, []);

  const onMenuIconClick = useCallback(() => {
    if (!isOpened) {
      setIsOpened(true);
    }
  }, [isOpened]);

  const renderMenu = (items: IMenuItem[]): React.ReactNode[] => {
    return items.map((item, index) => {
      if (item.isGroup && item.children.length) {
        return (
          <MainMenu.Group id={item.id} text={localize(item.title)} hidden={!userContext.isAuthorized(item.requiredAuthorizationData)} icon={item.icon} key={index}>
            {renderMenu(item.children)}
          </MainMenu.Group>
        );
      } else {
        return (
          <MainMenu.Item
            id={item.id}
            text={localize(item.title)}
            key={index}
            onClick={onMainMenuItemClick}
            hidden={!userContext.isAuthorized(item.requiredAuthorizationData)}
            onClickProps={{ path: item.path, id: item.id }}
            icon={item.icon}
          />
        );
      }
    });
  };

  return (
    <div className="main-menu-container">
      <Tippy
        appendTo="parent"
        animation="shift-away"
        boundary="scrollParent"
        content={
          <MainMenu selectedItemId={activeMenuItemId} {...{ className: styles.base }}>
            {renderMenu(menuItems.groupedList)}
          </MainMenu>
        }
        distance={0}
        duration={200}
        flip={false}
        interactive
        maxWidth={1200}
        theme="light"
        placement="bottom-end"
        trigger="click"
        popperOptions={{
          positionFixed: true
        }}
        onHide={onHide}
      >
        <Clickable className={clsx(styles.menuButton, isOpened && styles.open)} onClick={onMenuIconClick} />
      </Tippy>
    </div>
  );
};
